import Analytics from '@monolith/legacy/services/analytics';
import { CatalogFiltersEvent } from '@monolith/legacy/services/analytics/events/catalog-filters/catalog-filters.event';
import { CatalogFilterEventName } from '@monolith/legacy/services/analytics/properties/catalog-filters-property';
import { CatalogSortEvent } from '@monolith/legacy/services/analytics/events/catalog-sort/catalog-sort.event';
import { CatalogSortEventName } from '@monolith/legacy/services/analytics/properties/catalog-sort-property';
import Product, { ProductHit } from '@monolith/legacy/types/product';
import {
  BrandHit,
  NormalTileType,
  ProductTile,
  BrandTile,
  ESProductHit,
  ESSponsoredProductHit,
} from '@bc/discovery/domain/catalog';
import {
  trackProductHitsUpdated,
  trackBrandHitsUpdated,
  trackBrandClickedInProductTileSegment,
} from './catalog-browser-tracking';
import Store from '@monolith/legacy/store';

import { Config, SnippetTemplate, Context } from '@bc/discovery/domain/catalog';
import { isEqual, memoize } from '@core/utilities/lodash-utils';
import { ProductProperty, createProductProperty } from '@monolith/legacy/services/analytics/properties/product-property';
import { BrandProperty } from '@monolith/legacy/services/analytics/properties/brand-property';
import { ProductQuickAddToCartEvent } from '@monolith/legacy/services/analytics/events/product-quick-added-to-cart.event';
import UserClick from '@monolith/legacy/services/analytics/events/user-click.event';
import { CatalogTrackingData } from '../composables/use-catalog-tracking-data';
import type { RefinementsWithCount, FacetRefinement, NumericRefinement } from '../types/ais-state';
import { FacetRefinementNames } from '../constants/facet-refinement-names';
import { SearchParameters } from 'algoliasearch-helper';
import { DeepReadonly, Ref } from 'vue';
import { getNativeAdClickOrigin, SponsoredProductTile, trackProductClickWithAardvarkService } from '@bc/advertisement';
import { CatalogueType, TrackingService, SectionType } from '@bc/discovery/domain/tracking';
import { SearchCategory } from '@monolith/legacy/types/api/category';
import { SectionCreatedEvent } from '@monolith/legacy/services/analytics/events/section_created.event';
import { type IdPosition, getSectionId } from '@bc/shared';

type FacetRefinementWithType = { name: string; value: SearchParameters.FacetList; type: FacetRefinementNames };
type NumericRefinementWithType = { name: string; value: SearchParameters.OperatorList; type: FacetRefinementNames };
type RefinementWithType = FacetRefinementWithType | NumericRefinementWithType;

type TrackingDisjunctiveOrHierarchicalFacetRefinement = {
  name: string;
  value: string[] | number[];
};

type TrackingNumericRefinement = {
  name: string;
  value: { min?: number; max?: number };
};

type RefinementItem = TrackingDisjunctiveOrHierarchicalFacetRefinement | TrackingNumericRefinement;

export const contextComponentMap: Record<Context, string> = {
  [Context.BRAND]: 'brand_page',
  [Context.SEARCH]: 'search_page',
  [Context.COLLECTION_PRODUCTS]: 'collection_products_page',
  [Context.COLLECTION_BRANDS]: 'collection_brands_page',
  [Context.CATEGORY_PRODUCTS]: 'category_page',
  [Context.CATEGORY_BRANDS]: 'category_page',
  [Context.NEW_PRODUCTS]: 'new',
  [Context.NEW_BRANDS]: 'new',
  [Context.WHOLESALE]: 'wholesale',
};

export const catalogTypeMap: Record<Context, CatalogueType> = {
  [Context.BRAND]: CatalogueType.BrandSearch,
  [Context.SEARCH]: CatalogueType.Search,
  [Context.COLLECTION_PRODUCTS]: CatalogueType.Collection,
  [Context.COLLECTION_BRANDS]: CatalogueType.Collection,
  [Context.CATEGORY_PRODUCTS]: CatalogueType.Category,
  [Context.CATEGORY_BRANDS]: CatalogueType.Category,
  [Context.NEW_PRODUCTS]: CatalogueType.New,
  [Context.NEW_BRANDS]: CatalogueType.New,
  [Context.WHOLESALE]: CatalogueType.Wholesale,
};

const mapDisjunctiveRefinement = (
  refinement: FacetRefinementWithType,
  trackingName: string
): TrackingDisjunctiveOrHierarchicalFacetRefinement => ({
  name: trackingName,
  value: refinement.value,
});

// There's a discrepancy between the way the API returns the value for
// hierarchical refinements and the way it's supposed to be as per AIS docs.
//
// * We are getting an array with one element that contains the full path separated by '>'
//
// We should (?) be getting algoliasearchHelperSearchParameters.HierarchicalFacet
//
// type HierarchicalFacet = {
//   name: string;
//   attributes: string[];
//   separator?: string;
//   rootPath?: string | null;
//   showParentLevel?: boolean;
// };
const mapHierarchicalRefinement = (
  refinement: FacetRefinementWithType,
  trackingName: string
): TrackingDisjunctiveOrHierarchicalFacetRefinement => ({
  name: trackingName,
  value: refinement.value[0]?.split('>').map((value) => value.trim?.()) ?? [],
});

const mapNumericRefinement = (refinement: NumericRefinementWithType, trackingName: string): TrackingNumericRefinement => ({
  name: trackingName,
  value: {
    min: refinement.value['>=']?.[0] as number,
    max: refinement.value['<=']?.[0] as number,
  },
});

const mapRefinement = (refinement: RefinementWithType, trackingName: string): RefinementItem => {
  switch (refinement?.type) {
    case FacetRefinementNames.DisjunctiveFacetsRefinements:
      return mapDisjunctiveRefinement(refinement as FacetRefinementWithType, trackingName);
    case FacetRefinementNames.HierarchicalFacetsRefinements:
      return mapHierarchicalRefinement(refinement as FacetRefinementWithType, trackingName);
    case FacetRefinementNames.NumericRefinements:
      return mapNumericRefinement(refinement as NumericRefinementWithType, trackingName);
    default:
      return { name: trackingName, value: [] };
  }
};

const FACET_REFINEMENT_NAMES = new Set<string>(Object.values(FacetRefinementNames));

export const mapRefinements = (config: DeepReadonly<Config>, refinementsWithCount: RefinementsWithCount) => {
  const refinements = Object.entries(refinementsWithCount)
    .filter((item): item is [FacetRefinementNames, FacetRefinement | NumericRefinement] => FACET_REFINEMENT_NAMES.has(item[0]))
    .reduce<RefinementWithType[]>((accumulator, [refinementType, refinement]) => {
      Object.entries(refinement ?? {}).forEach(([name, value]) => {
        if (refinementType === FacetRefinementNames.NumericRefinements) {
          accumulator.push({ name, value: value as NumericRefinement, type: refinementType });
        } else {
          accumulator.push({ name, value: value as FacetRefinement, type: refinementType });
        }
      });
      return accumulator;
    }, [] as RefinementWithType[]);

  const facets = config.Facets?.filter((facet) => facet.display).flatMap((facet) => ('facets' in facet ? facet.facets : facet));
  return facets.map((facet) => {
    const refinement = refinements.find(({ name }) => name === facet.name);
    return mapRefinement(refinement, facet.trackingName);
  });
};

const findFirstChangedFilter = (currentRefinements: RefinementItem[], previousRefinements: RefinementItem[]) => {
  if (!previousRefinements) {
    return undefined;
  }
  return currentRefinements.find((currentRefinement) => {
    const previousRefinement = previousRefinements.find(
      (previousRefinement) => previousRefinement.name === currentRefinement.name
    );
    return !previousRefinement || !isEqual(currentRefinement.value, previousRefinement.value);
  });
};

export const _createFiltersTracker = (catalogTrackingData: CatalogTrackingData) => {
  let previousRefinements: RefinementItem[];

  return (refinements: RefinementsWithCount) => {
    const trackingRefinements = mapRefinements(catalogTrackingData.config.value, refinements);
    const changedRefinement = findFirstChangedFilter(trackingRefinements, previousRefinements);

    // Avoid dispatching an event with no filters applied when page loads.
    if (refinements.refinementCount || previousRefinements) {
      // The discount facet's own value, flattened out of `filters` so analytics tools can group
      // by it directly - `filters` is a nested array, and grouping by "the value of the entry
      // named maxDiscountRate" isn't something most analytics tools' property breakdowns support.
      const discountTier =
        changedRefinement && isDiscountFacetTrackingName(changedRefinement.name) && Array.isArray(changedRefinement.value)
          ? (changedRefinement.value[0] as string | undefined) ?? null
          : null;

      Analytics.track(
        new CatalogFiltersEvent({
          name: CatalogFilterEventName.FilterApplied,
          properties: {
            filter: changedRefinement?.name,
            filters: trackingRefinements,
            filtersQuery: refinements.filters,
            discount_tier: discountTier,
          },
        })
      );
    }

    previousRefinements = trackingRefinements;
  };
};

const isSponsoredProductTile = (hit: ProductHit) => 'ad' in hit && hit.ad;

export const mapProductHits = (hits: ProductHit[]): Array<ProductTile | SponsoredProductTile> =>
  hits.map((hit) => ({
    type: isSponsoredProductTile(hit) ? NormalTileType.SponsoredProduct : NormalTileType.Product,
    product: hit,
    id: hit.id,
  }));

export const mapBrandHits = (hits: BrandHit[]): BrandTile[] =>
  hits.map((hit) => ({
    type: NormalTileType.Brand,
    brand: hit,
    id: hit.id,
  }));

const _trackProductSection = (
  catalogTrackingData: CatalogTrackingData,
  trackingRefinements: RefinementItem[],
  page: number,
  hits: ProductHit[]
) => {
  trackProductHitsUpdated({
    valueProposition: {
      value_proposition: catalogTrackingData.valueProposition.value,
      value_proposition_id: catalogTrackingData.valuePropositionId?.value,
    },
    catalogueType: catalogTrackingData.catalogType.value,
    view: 0,
    pageIndex: page,
    sectionId: catalogTrackingData.trackingSectionId.value,
    filters: trackingRefinements,
    // This is a partial object of SearchCategory, but it's enough for the event.
    // trackProductHitsUpdated is expecting a SearchCategory object, but it only uses full_name.
    filterState: { selectedCategories: [{ full_name: catalogTrackingData.selectedCategoryName?.value } as SearchCategory] },
    tiles: mapProductHits(hits),
  });
};

const _trackBrandSection = (
  catalogTrackingData: CatalogTrackingData,
  trackingRefinements: RefinementItem[],
  page: number,
  hits: BrandHit[]
) => {
  trackBrandHitsUpdated({
    valueProposition: {
      value_proposition: catalogTrackingData.valueProposition.value,
      value_proposition_id: catalogTrackingData.valuePropositionId?.value,
    },
    catalogueType: catalogTrackingData.catalogType.value,
    view: 1,
    pageIndex: page,
    sectionId: catalogTrackingData.trackingSectionId.value,
    filters: trackingRefinements,
    tiles: mapBrandHits(hits),
  });
};

// The "Discount" facet is either the original `discount` facet or its `maxDiscountRate`
// successor. They are usually mutually exclusive (gated by the disable_PRICE-5_max_discount_rate_facet
// kill switch), but CatalogFacetConfigService lists both together for SEARCH/CATEGORY_PRODUCTS
// when the switch is off, and CatalogFacetClient only drops the legacy one when there's no active
// discount event period - so both can genuinely coexist. Exported so quick-filters-bar.vue and
// filter-sidebar.vue can share this instead of keeping their own copies in sync by hand.
export const isDiscountFacetTrackingName = (trackingName: string) => trackingName === 'discount' || trackingName === 'maxDiscountRate';

export const DISCOUNT_SORT_VALUE = 'max_discount_rate:desc';

// Uses .some() rather than .find() + a single check: with both facets present, .find() would
// short-circuit on whichever comes first (the legacy `discount` facet, per
// CatalogFacetConfigService) and only ever test that one - reporting false even when the other
// facet is the one actually refined.
export const isDiscountFilterCurrentlyActive = (config: DeepReadonly<Config>, refinements: RefinementsWithCount): boolean =>
  mapRefinements(config, refinements).some(
    (refinement) => isDiscountFacetTrackingName(refinement.name) && Array.isArray(refinement.value) && refinement.value.length > 0
  );

export const isDiscountSortCurrentlyActive = (sortBy: string | undefined): boolean => sortBy === DISCOUNT_SORT_VALUE;

export type DiscountTrackingState = {
  refinements: Ref<RefinementsWithCount>;
  sortBy: Ref<string | undefined>;
};

export const getCatalogTrackingHandlers = (
  catalogTrackingData: CatalogTrackingData,
  discountTrackingState?: DiscountTrackingState
) => {
  const filtersTracker = _createFiltersTracker(catalogTrackingData);

  // Computed fresh at call time (not stamped-and-reset) since the discount filter/sort is
  // persistent catalog state, not a one-shot click - a retailer can browse and click several
  // products in a row while it stays active.
  const getDiscountInterestProperties = () => ({
    discount_filter_active: discountTrackingState
      ? isDiscountFilterCurrentlyActive(catalogTrackingData.config.value, discountTrackingState.refinements.value)
      : false,
    discount_sort_active: discountTrackingState ? isDiscountSortCurrentlyActive(discountTrackingState.sortBy.value) : false,
  });

  // Stamped unconditionally on every product click (mirroring fromSectionId), including with
  // null - otherwise re-clicking the same product after the discount filter/sort was cleared
  // would leave an earlier, now-stale 'filter'/'sort' stamp for that uuid unoverwritten.
  const stampDiscountIntent = (
    product: ESProductHit,
    discountInterest: { discount_filter_active: boolean; discount_sort_active: boolean }
  ) => {
    Store.dispatch('tracking/setDiscountIntent', {
      productUuid: product.uuid,
      discountIntent: discountInterest.discount_filter_active ? 'filter' : discountInterest.discount_sort_active ? 'sort' : null,
    });
  };

  const trackSectionCreated = (refinements: RefinementsWithCount, page: number, hits: ProductHit[] | BrandHit[]) => {
    const trackingRefinements = mapRefinements(catalogTrackingData.config.value, refinements);

    const isProductSection = catalogTrackingData.config.value.template === SnippetTemplate.PRODUCTS;
    const isBrandSection = catalogTrackingData.config.value.template === SnippetTemplate.BRANDS;
    if (isProductSection) {
      _trackProductSection(catalogTrackingData, trackingRefinements, page, hits as ProductHit[]);
    }

    if (isBrandSection) {
      _trackBrandSection(catalogTrackingData, trackingRefinements, page, hits as BrandHit[]);
    }
  };

  const trackBrandSuggestionClicked = async (trackingData: { brandId: number }) => {
    const payload = {
      component: catalogTrackingData.component.value,
      action: 'brand_suggestion_clicked_top_page',
      itemType: 'brand',
      id_section: catalogTrackingData.trackingSectionId.value,
      brandId: trackingData.brandId,
    };
    await Analytics.track(new UserClick(payload));
  };

  const trackTileProductClicked = async (trackingData: { page: number; product: ESProductHit; position: number }) => {
    Store.dispatch('tracking/setFromSectionId', catalogTrackingData.trackingSectionId.value);
    const discountInterest = getDiscountInterestProperties();
    stampDiscountIntent(trackingData.product, discountInterest);
    const payload = {
      component: catalogTrackingData.component.value,
      action: 'product_clicked',
      itemType: 'product',
      id_section: catalogTrackingData.trackingSectionId.value,
      page_number: trackingData.page,
      productId: trackingData.product.id,
      position: trackingData.position,
      ...discountInterest,
    };
    await Analytics.track(new UserClick(payload));
  };

  const trackDrawerClicked = async (trackingData: { page: number; product: ESProductHit; position: number }) => {
    Store.dispatch('tracking/setFromSectionId', catalogTrackingData.trackingSectionId.value);
    const discountInterest = getDiscountInterestProperties();
    stampDiscountIntent(trackingData.product, discountInterest);
    const payload = {
      component: catalogTrackingData.component.value,
      action: 'drawer_product_clicked',
      itemType: 'product',
      id_section: catalogTrackingData.trackingSectionId.value,
      page_number: trackingData.page,
      productId: trackingData.product.id,
      position: trackingData.position,
      ...discountInterest,
    };
    await Analytics.track(new UserClick(payload));
  };

  const trackTileBrandClicked = async (trackingData: { page: number; brandId: number; position: number }) => {
    const payload = {
      component: catalogTrackingData.component.value,
      action: 'brand_clicked',
      itemType: 'brand',
      id_section: catalogTrackingData.trackingSectionId.value,
      page_number: trackingData.page,
      brandId: trackingData.brandId,
      position: trackingData.position,
    };
    await Analytics.track(new UserClick(payload));
  };

  const trackTileSponsoredProductClicked = async (trackingData: {
    position: number;
    page: number;
    eventTarget: Element;
    product: ESSponsoredProductHit;
  }) => {
    await Analytics.track(
      new UserClick({
        component: 'native_ads',
        action: 'product_clicked',
        itemType: 'product',
        productId: trackingData.product.id,
        position: trackingData.position,
        page_number: trackingData.page,
        id_section: catalogTrackingData.trackingSectionId.value,
      })
    );
    const adId = trackingData.product.aardvarkServeId || trackingData.product.ad.metadata.ad_id;
    await trackProductClickWithAardvarkService(adId, getNativeAdClickOrigin(trackingData.eventTarget));
  };

  const trackProductSnippetBrandClicked = async (trackingData: {
    brandId: number;
    position: number;
    page: number;
    brandTags: string[];
    isSponsoredTile: boolean;
    product: Product | (ProductHit & { categoryName?: string }) | (Product & { isChecked: boolean });
  }) => {
    const payload = {
      component: trackingData.isSponsoredTile ? 'native_ads' : catalogTrackingData.component.value,
      brandId: trackingData.brandId,
      position: trackingData.position,
      page: trackingData.page,
      brandTags: trackingData.brandTags,
      id_section: catalogTrackingData.trackingSectionId.value,
      productId: trackingData.product.id,
    };
    await trackBrandClickedInProductTileSegment(payload);
  };

  const trackQuickAddToCartClicked = async (trackingData: {
    page: number;
    productId: number;
    position: number;
    isSponsoredTile?: boolean;
    component?: string;
  }) => {
    const payload = {
      component: trackingData.component ?? (trackingData.isSponsoredTile ? 'native_ads' : catalogTrackingData.component.value),
      action: 'quick_add_to_cart_clicked',
      itemType: 'product',
      id_section: catalogTrackingData.trackingSectionId.value,
      page_number: trackingData.page,
      productId: trackingData.productId,
      position: trackingData.position,
    };
    await Analytics.track(new UserClick(payload));
  };

  const trackQuickAddToCartProductAdded = async (trackingData: {
    productProperty: ProductProperty;
    brandProperty: BrandProperty;
    page: number;
    quantity: number;
  }) => {
    const payload: [ProductProperty, BrandProperty, number, number, string] = [
      trackingData.productProperty,
      trackingData.brandProperty,
      trackingData.page,
      trackingData.quantity,
      catalogTrackingData.trackingSectionId.value,
    ];
    await Analytics.track(new ProductQuickAddToCartEvent(...payload));
  };

  const trackProductAddedToCart = (payload: {
    option: { id: number; name: string };
    quantity: number;
    product: ESProductHit;
    position: number;
    pageNumber: number;
  }) => {
    trackQuickAddToCartProductAdded({
      productProperty: createProductProperty({
        id: payload.product.id,
        name: payload.product.name as string,
        wholesalePrice: payload.product.wholesale_price,
        position: payload.position,
        option: payload.option,
        uuid: payload.product.uuid,
      }),
      brandProperty: { id: payload.product.brand.id, name: payload.product.brand.name },
      page: payload.pageNumber,
      quantity: payload.quantity * payload.product.unit_multiplier,
    });
  };

  const trackRelatedCollectionClicked = async (trackingData: { collectionId: string; itemType: string }) => {
    TrackingService.trackRelatedCollectionClicked(
      catalogTrackingData.trackingSectionId.value,
      trackingData.itemType,
      trackingData.collectionId
    );
  };

  const trackFilterInfoCtaClicked = async (filterTrackingId: string) => {
    await Analytics.track(
      new UserClick({
        component: catalogTrackingData.component.value,
        id_section: catalogTrackingData.trackingSectionId.value,
        action: 'info_tooltip_cta_clicked',
        id_filter: filterTrackingId,
      })
    );
  };

  // FilterPanelClicked already existed in CatalogFilterEventName but had no caller - reused here
  // rather than adding a new event name, since it fits this exact "filter panel opened" gap.
  const trackFilterPanelOpened = async (filterTrackingName: string) => {
    await Analytics.track(
      new CatalogFiltersEvent({
        name: CatalogFilterEventName.FilterPanelClicked,
        properties: { filter: filterTrackingName },
      })
    );
  };

  // Fires for every sort change, not just discount - mirrors FilterApplied's own convention of
  // tracking generically with a distinguishing property, letting analytics filter on
  // sortBy: 'max_discount_rate:desc' downstream.
  const trackSortApplied = async (sortBy: string) => {
    await Analytics.track(
      new CatalogSortEvent({
        name: CatalogSortEventName.SortApplied,
        properties: {
          sortBy,
          component: catalogTrackingData.component.value,
          id_section: catalogTrackingData.trackingSectionId.value,
        },
      })
    );
  };

  return {
    filtersTracker,
    trackSectionCreated,
    trackBrandSuggestionClicked,
    trackTileProductClicked,
    trackTileBrandClicked,
    trackTileSponsoredProductClicked,
    trackProductSnippetBrandClicked,
    trackQuickAddToCartClicked,
    trackQuickAddToCartProductAdded,
    trackProductAddedToCart,
    trackRelatedCollectionClicked,
    trackFilterInfoCtaClicked,
    trackFilterPanelOpened,
    trackSortApplied,
    trackDrawerClicked
  };
};

// SimilarProducts

type TrackSimilarProductsSectionCreatedPayload = {
  products: Product[];
};

type TrackSimilarProductsButtonClickedPayload = {
  productId: number;
  sectionId: string;
  component: string;
  pageNumber: number;
  position: number;
};

type TrackSimilarProductsTileClickedPayload = {
  productId: number;
  brandId?: number;
  position: number;
  action: string;
};

type TrackQuickAddToCartClickedPayload = {
  productId: number;
  position: number;
};

export const getSimilarProductsRecommendationTracking = memoize((trackingData: { productId: number }) => {
  const valueProposition = `similar_products_drawer_${trackingData.productId}`;
  const sectionType = SectionType.RecoCarousel;
  const sectionId = getSectionId({
    valueProposition,
    sectionType,
  });

  return {
    drawerSectionId: sectionId,
    trackSimilarProductSectionCreated: (payload: TrackSimilarProductsSectionCreatedPayload) => {
      const id_product: IdPosition[] = payload.products.map((product, index) => ({ id: product.id, position: index }));
      Analytics.track(
        new SectionCreatedEvent({
          id_section: sectionId,
          section_type: sectionType,
          value_proposition: valueProposition,
          itemtype: 'product',
          id_product,
        })
      );
    },
    trackShowSimilarProductsButtonClicked: (payload: TrackSimilarProductsButtonClickedPayload) => {
      const clickData = {
        id_section: payload.sectionId,
        component: payload.component,
        action: 'similar_product_icon_clicked',
        itemtype: 'product',
        page_number: payload.pageNumber,
        position: payload.position,
        id_product: trackingData.productId,
      };
      Analytics.track(new UserClick(clickData));
    },
    trackSimilarProductTileClick: (payload: TrackSimilarProductsTileClickedPayload) => {
      Analytics.track(
        new UserClick({
          ...payload,
          id_section: sectionId,
          component: sectionType,
          itemType: 'product',
        })
      );
    },
    trackQuickAddToCartClicked: (payload: TrackQuickAddToCartClickedPayload) => {
      Analytics.track(
        new UserClick({
          ...payload,
          id_section: sectionId,
          component: sectionType,
          action: 'quick_add_to_cart_clicked',
          itemType: 'product',
        })
      );
    },
  };
});
